import { useState, useEffect } from 'react'
import { Info, GitBranch, Calendar, Code } from 'lucide-react'

interface VersionData {
  version: string
  buildDate: string
  buildTimestamp: string
  appName: string
  environment: string
}

export function VersionInfo({ 
  className = '',
  showDetails = false,
  position = 'footer' // 'footer', 'header', 'sidebar'
}: {
  className?: string
  showDetails?: boolean
  position?: 'footer' | 'header' | 'sidebar'
}) {
  const [versionData, setVersionData] = useState<VersionData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setVersionData({
      version: __APP_VERSION__,
      buildDate: __BUILD_DATE__,
      buildTimestamp: Date.now().toString(),
      appName: import.meta.env.VITE_APP_NAME || 'TSA Gardening App',
      environment: import.meta.env.VITE_ENV || 'development'
    })
    setLoading(false)
  }, [])

  if (loading) {
    return (
      <div className={`version-info loading ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-nature-200 dark:bg-nature-700 rounded w-20"></div>
        </div>
      </div>
    )
  }

  if (!versionData) {
    return null
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('sv-SE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }



  const getEnvironmentBadge = (env: string) => {
    const colors = {
      production: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      staging: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      development: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    }
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[env as keyof typeof colors] || colors.development}`}>
        {env}
      </span>
    )
  }

  // Compact footer version
  if (position === 'footer' && !showDetails) {
    return (
      <div className={`version-info footer ${className}`}>
        <div className="flex items-center space-x-2 text-xs text-nature-500 dark:text-nature-400">
          <Code className="h-3 w-3" />
          <span>v{versionData.version}</span>
          <span>•</span>
          {getEnvironmentBadge(versionData.environment)}
        </div>
      </div>
    )
  }

  // Detailed version display
  return (
    <div className={`version-info detailed ${className}`}>
      <div className="bg-nature-50 dark:bg-nature-800 border border-nature-200 dark:border-nature-700 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Info className="h-5 w-5 text-nature-500" />
          <h3 className="text-sm font-medium text-nature-700 dark:text-nature-300">
            Version Information
          </h3>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center space-x-2">
            <GitBranch className="h-4 w-4 text-nature-400" />
            <span className="text-nature-600 dark:text-nature-400">Version:</span>
            <span className="font-mono font-medium text-nature-800 dark:text-nature-200">
              {versionData.version}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-nature-400" />
            <span className="text-nature-600 dark:text-nature-400">Build:</span>
            <span className="text-nature-800 dark:text-nature-200">
              {formatDate(versionData.buildDate)}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Code className="h-4 w-4 text-nature-400" />
            <span className="text-nature-600 dark:text-nature-400">Environment:</span>
            {getEnvironmentBadge(versionData.environment)}
          </div>
        </div>
        
        {showDetails && (
          <div className="mt-3 pt-3 border-t border-nature-200 dark:border-nature-700">
            <div className="text-xs text-nature-500 dark:text-nature-400">
              <div>App: {versionData.appName}</div>
              <div>Build ID: {versionData.buildTimestamp}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
